import { LayerType } from '../../shared/model/layers/layer-types'
import {
    createAnnotationShader,
    createMarkerShader,
    createSelectionsShader,
    createRGBShader,
} from '../../shared/utils/shaders'
import {
    LayerConfig,
    LayerGroup,
    LayerGroupApiResponse,
    LayerIdApiResponse,
    LARGE_GROUP_THRESHOLD,
    NeuroglancerConfig,
    StaticLayerConfig,
    ViewerConfigApiRequest,
    ViewerConfigApiResponse,
    ViewerPosition,
    ViewerState,
} from './viewer.state'
import { isManagedLayer, isSelectionAnnotationLayer } from './viewer-layer-utils'

function fixZoom(zoom: number | null | undefined): number | null | undefined {
    if (zoom === 0) {
        console.error('loaded crossSectionScale 0 from API (should not happen), using 1 instead')
        return 1
    }
    return zoom
}

export const mergeStaticAndDynamicLayer = (
    staticLayer: StaticLayerConfig,
    dynamicLayer: LayerConfig | undefined
): LayerConfig => (dynamicLayer ? { ...staticLayer, ...dynamicLayer } : staticLayer)

/** Layer IDs belonging to groups with more than LARGE_GROUP_THRESHOLD layers. */
export const getLargeGroupLayerIdSet = (layerGroups: LayerGroup[]): Set<string> => {
    const ids = new Set<string>()
    const collect = (group: LayerGroup): void => {
        if (group.layerIds.length > LARGE_GROUP_THRESHOLD) {
            group.layerIds.forEach(id => ids.add(id))
        }
        group.children.forEach(collect)
    }
    layerGroups.forEach(collect)
    return ids
}

/** Coerce API expandedNodes (may be numeric group ids) to strings for UI and save. */
export const normalizeExpandedNodes = (nodes: readonly unknown[] | undefined | null): string[] =>
    (nodes ?? [])
        .map(node => (node === null || node === undefined ? '' : String(node)))
        .filter(node => node.length > 0)

export const getLoadedManagedLayerIds = (config: ViewerState): string[] =>
    (config.layers ?? [])
        .filter((layer): layer is LayerConfig & { id: string } => !!layer.id && isManagedLayer(layer))
        .map(layer => layer.id)

/** Map each layer id in a large group to that group's id. */
export const getLargeGroupLayerToGroupIdMap = (layerGroups: LayerGroup[]): Map<string, string> => {
    const map = new Map<string, string>()
    const collect = (group: LayerGroup): void => {
        const groupId = group.id
        if (group.layerIds.length > LARGE_GROUP_THRESHOLD && groupId) {
            group.layerIds.forEach(id => map.set(id, groupId))
        }
        group.children.forEach(collect)
    }
    layerGroups.forEach(collect)
    return map
}

const inferLegacyLoadedLayerIds = (
    config: ViewerConfigApiResponse,
    layerGroups: LayerGroup[]
): string[] => {
    const largeGroupLayerIds = getLargeGroupLayerIdSet(layerGroups)
    return config.state.layers
        .map(layer => layer.id)
        .filter((id): id is string => !!id && !largeGroupLayerIds.has(id))
}

/** API sends favoriteLayers as string[], [], or null/omitted when never set. */
const getApiFavoriteLayers = (apiState: {
    favoriteLayers?: string[] | null
}): string[] | undefined => {
    if (!Array.isArray(apiState.favoriteLayers)) {
        return undefined
    }
    return apiState.favoriteLayers
}

const resolveFavoriteLayers = (
    config: ViewerConfigApiResponse,
    layerGroups: LayerGroup[]
): string[] => {
    const apiState = config.state as typeof config.state & { favoriteLayers?: string[] | null }
    const largeGroupLayerIds = getLargeGroupLayerIdSet(layerGroups)

    const explicit = getApiFavoriteLayers(apiState)
    if (explicit !== undefined) {
        // First-class favoriteLayers from API — trust explicit list (including large-group
        // layers loaded with visible:false). [] means intentionally unloaded.
        return explicit
    }

    const persisted = inferLegacyLoadedLayerIds(config, layerGroups)
    // Legacy large groups: state.layers may hold default viewstate for the whole catalog
    // (e.g. visible:false). Only visible:true means the layer was actively loaded.
    const largeGroupFromStateLayers = apiState.layers
        .filter(
            (layer): layer is typeof layer & { id: string } =>
                !!layer.id && largeGroupLayerIds.has(layer.id) && layer.visible === true
        )
        .map(layer => layer.id)
    return [...new Set([...persisted, ...largeGroupFromStateLayers])]
}

export const mapApiResponseToViewerState = (config: ViewerConfigApiResponse): ViewerState => {
    let layerGroups: LayerGroup[]
    try {
        layerGroups = mapApiResponseToLayerGroups(config.state.groups)
    } catch (e: unknown) {
        console.warn('Failed to parse layer groups:', e)
        layerGroups = mapApiResponseToLayerGroups([
            {
                name: 'WorkaroundGroup',
                children: config.data.layers
                    .filter(layer => layer.id)
                    .map(layer => ({ id: layer.id! })),
                group_id: 'WorkaroundGroup',
            },
        ])
    }

    const favoriteLayers = resolveFavoriteLayers(config, layerGroups)
    const persistedDynamicLayers = (config.state.layers as LayerConfig[]) ?? []

    const dynamicLayersById = new Map(
        persistedDynamicLayers
            .filter((layer): layer is LayerConfig & { id: string } => !!layer.id)
            .map(layer => [layer.id, layer])
    )
    const layers = config.data.layers
        .map((staticLayer: StaticLayerConfig): LayerConfig | null => {
            const dynamicLayer = staticLayer.id
                ? dynamicLayersById.get(staticLayer.id)
                : undefined
            return mergeStaticAndDynamicLayer(staticLayer, dynamicLayer)
        })
        .filter((layer: LayerConfig): layer is LayerConfig => layer !== null)

    return {
        dimensions: config.data.dimensions,
        position: config.state.position,
        crossSectionScale: fixZoom(config.state.crossSectionScale),
        layout: config.state.layout ?? 'xy',
        expandedNodes: normalizeExpandedNodes(config.state.expandedNodes),
        layers: layers,
        layerGroups,
        invisibleLayers: [],
        favoriteLayersFieldPresent: Array.isArray(
            (config.state as { favoriteLayers?: string[] | null }).favoriteLayers
        ),
        explicitFavoriteLayers:
            getApiFavoriteLayers(config.state as { favoriteLayers?: string[] | null }) ?? [],
        favoriteLayers,
        persistedDynamicLayers,
        /** Raw state.layers length from API (NgRx-only; detects legacy saves without favoriteLayers). */
        hasPersistedLayerStateInApi: (config.state.layers?.length ?? 0) > 0,
        originalLayers: config.data.layers,
        searchKey: '',
        layerListDialogOpen: false,
    }
}

const mapApiResponseToLayerGroups = (groups: LayerGroupApiResponse[]): LayerGroup[] => {
    const mapTreeRecursively = (group: LayerGroupApiResponse): LayerGroup => ({
        id: String(group.group_id || group.name),
        name: group.name,
        layerIds: group.children.filter(isLayerIdApiResponse).map(layer => layer.id),
        children: group.children
            .filter(isLayerGroupApiResponse)
            .map(group => mapTreeRecursively(group)),
    })
    return groups.map(group => mapTreeRecursively(group))
}

const isLayerIdApiResponse = (
    value: LayerGroupApiResponse | LayerIdApiResponse
): value is LayerIdApiResponse => {
    return 'id' in value && !('children' in value)
}

const isLayerGroupApiResponse = (
    value: LayerGroupApiResponse | LayerIdApiResponse
): value is LayerGroupApiResponse => {
    return 'children' in value && !('id' in value)
}

export const mapViewerStateToNeuroglancerConfig = async (
    config: ViewerState
): Promise<NeuroglancerConfig> => {
    const layersForViewer = ensureSelectableAnnotationLayersLoaded(config)

    return {
        dimensions: config.dimensions,
        crossSectionScale: config.crossSectionScale || undefined,
        layout: config.layout ?? 'xy',
        position: isValidPosition(config.position) ? config.position : undefined,
        showAxisLines: false,
        layers: await Promise.all(
            layersForViewer
                .filter((layer: LayerConfig) => layer.source?.startsWith('precomputed://'))
                .map(async (layer: LayerConfig) => ({
                    ...layer,
                    [layer.type === LayerType.Segmentation ? 'selectedAlpha' : 'opacity']:
                        layer.opacity ?? 1,
                    ...(layer.segmentationColor !== undefined &&
                        layer.segmentationColor !== null && {
                            segmentDefaultColor: layer.segmentationColor,
                        }),
                    shader: isMarkerLayer(layer)
                        ? (await isRawImageLayer(layer))
                            ? createRGBShader()
                            : createMarkerShader(layer.shaderColor ?? null)
                        : isSelectionsLayer(layer)
                        ? createSelectionsShader()
                        : isAnnotationLayer(layer)
                        ? createAnnotationShader(layer.opacity, layer.shaderColor)
                        : layer.shader,
                }))
        ),
    }
}

const isValidPosition = (pos: ViewerPosition | undefined | null): boolean => {
    return (
        pos !== null &&
        pos !== undefined &&
        pos.length === 3 &&
        pos[0] !== null &&
        pos[0] !== undefined &&
        pos[1] !== null &&
        pos[1] !== undefined &&
        pos[2] !== null &&
        pos[2] !== undefined
    )
}

const isMarkerLayer = (layer: LayerConfig): boolean => {
    return layer.type === LayerType.Image
}

const isSelectionsLayer = (layer: LayerConfig): boolean => {
    return layer.type === LayerType.Annotation && !!layer.isSelectable
}

const isAnnotationLayer = (layer: LayerConfig): boolean => {
    return layer.type === LayerType.Annotation && !isSelectionAnnotationLayer(layer)
}

const ensureSelectableAnnotationLayersLoaded = (config: ViewerState): LayerConfig[] => {
    const layers = [...(config.layers ?? [])]
    const loadedIds = new Set(layers.map(l => l.id).filter((id): id is string => !!id))

    // Selection COM layers must stay visible; persisted/API state often has visible:false.
    for (let i = 0; i < layers.length; i++) {
        if (isSelectionAnnotationLayer(layers[i])) {
            layers[i] = { ...layers[i], visible: true }
        }
    }

    for (const selectableLayer of (config.originalLayers ?? []).filter(isSelectionAnnotationLayer)) {
        if (selectableLayer.id && !loadedIds.has(selectableLayer.id)) {
            layers.push({ ...selectableLayer, visible: true })
            loadedIds.add(selectableLayer.id)
        }
    }
    return layers
}

const isRawImageLayer = async (layer: LayerConfig): Promise<boolean> => {
    const info = await fetch(`${layer.source?.split('precomputed://')[1]}/info`)
    const numChannels = (await info.json()).num_channels
    if (numChannels === 3) {
        return true
    }
    return false
}

export const mapViewerStateToApiRequest = (config: ViewerState): ViewerConfigApiRequest => {
    const loadedManagedIds = new Set(getLoadedManagedLayerIds(config))

    return {
        position: config.position,
        layout: config.layout,
        expandedNodes: normalizeExpandedNodes(config.expandedNodes),
        crossSectionScale: config.crossSectionScale,
        groups: config.layerGroups,
        favoriteLayers: [...loadedManagedIds],
        layers:
            config.layers
                ?.filter(
                    layer =>
                        !!layer.id &&
                        (loadedManagedIds.has(layer.id) || isSelectionAnnotationLayer(layer))
                )
                .map(l => ({
                    id: l.id,
                    visible: l.visible ?? false,
                    shaderColor: l.shaderColor,
                    opacity: l.opacity,
                    opacityUnselected: l.opacityUnselected,
                    saturation: l.saturation,
                    brightness: l.brightness,
                    contrast: l.contrast,
                    normalize: l.normalize,
                    segmentationColor: l.segmentationColor ?? null,
                })) ?? [],
    }
}

/** Sorted loaded-layer IDs; used to detect stale in-flight Neuroglancer config builds. */
export const getLoadedLayerIdsFingerprint = (state: ViewerState): string =>
    (state.layers ?? [])
        .map(l => l.id)
        .filter((id): id is string => !!id)
        .sort()
        .join(',')

export const hasDisplayableLoadedLayers = (state: ViewerState): boolean =>
    hasDisplayableNeuroglancerLayers(state.layers)

export const hasDisplayableNeuroglancerLayers = (layers: LayerConfig[] | undefined): boolean =>
    (layers ?? []).some(
        layer => !!layer.source?.startsWith('precomputed://') && isManagedLayer(layer)
    )

export const getLayerIdsForGroupId = (groupId: string, state: ViewerState): string[] => {
    const layerIds: string[] = []

    const getLayerIdsRecursively = (group: LayerGroup, isSubgroupOfNameMatch = false): void => {
        if (isSubgroupOfNameMatch || group.id === groupId) {
            layerIds.push(...group.layerIds)
            isSubgroupOfNameMatch = true
        }
        group.children.forEach(subGroup => {
            getLayerIdsRecursively(subGroup, isSubgroupOfNameMatch)
        })
    }

    state.layerGroups.forEach(group => {
        getLayerIdsRecursively(group)
    })

    return layerIds
}
